import { Ref, onUnmounted, ComponentInternalInstance, getCurrentInstance } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入 compositions
import guideStepHandler from '@/renderer/components/GuideAndHelp/composition/guideStepHandler';

// 引入工具类
import { can } from '@/renderer/scripts/composition/authHandler';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import GenericStore from '@/renderer/scripts/utils/storage';
import ipc from '@/renderer/scripts/utils/ipc';
import {
  isAllowEditDoc,
  isAllowPreviewImage,
  isAllowPrivate,
  noPreview,
} from '@/renderer/scripts/composition/allowPreview';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { IS_BROWSER } from '@/common/constant';
import { DrawerEvents } from '@/renderer/store/modules/drawer';

type ComplexDynamicData = SMH.getDirectoryOrFileInfoData & SMH.DynamicData;
type Handler = {
  methods: {
    openPreview: (item: ComplexDynamicData, previewPage: number, historyId?: number, teamId?: number) => void;
    dynamicsDrawerOpen: (_path: string, action: 'add' | 'remove') => void;
    dynamicOpen: (item: ComplexDynamicData) => Promise<void>;
  };
};
export default function dynamicOpenHandler(spaceId?: Ref<string>): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const { $route, $router, $store } = vm;
  const { methods: guideStepMethods } = guideStepHandler();
  const methods = {
    async updateGuide() {
      // 查看新手引导是否有对应数据，如果没有，对其进行对应查看相关的更新
      guideStepMethods.monitoringStepsDone('personalSpace', 1);
    },
    /**
     * 浏览器端预览
     * @param item
     * @param previewPage
     * @param historyId
     * @param teamId
     */
    async openPreviewInBrowser(item: ComplexDynamicData, previewPage: number, historyId?: number, teamId?: number) {
      const _path = item.toObjectPath.split('/');
      const currPath = _path.slice(0, -1).join('/');
      const path = item.previewByDoc ? '/web/preview/doc' : '/web/preview/media';
      const query: Record<string, string | undefined> = {
        name: item.toObjectName,
        path: currPath,
        previewPage: `${previewPage}`,
        spaceId: (item as any).spaceId,
        from: vm.$route.name as string,
        spaceOrgId: item.orgId || $store.getters.orgId,
        spaceTag: item.spaceTag,
      };

      // 预览图片列表时，分享链接根目录需要单独处理路径
      if (vm.$route.name === 'share' && !item.previewByDoc && item.fileType === 'image') {
        query.imgShareRootPath = currPath;
        const rootPath = GenericStore.get<string[]>(GenericStore.StoreKey.SHARE_LINK_ROOT_PATH) || [];
        if (Array.isArray(rootPath) && rootPath.join('/') === currPath) {
          query.imgShareRootPath = '';
        }
      }
      const shareToken = (vm.$route.params?.shareToken as string) || (vm.$route.query?.shareToken as string);
      shareToken && (query.shareToken = shareToken as string);
      historyId && (query.historyId = String(historyId));
      teamId && (query.teamId = String(teamId));
      const newpage = vm.$router?.resolve({
        path,
        query,
      });
      if ($store.getters.isInWeMeetApp || $store.getters.isTclApp) {
        const currRoute = {
          name: vm.$route.name,
          query: vm.$route.query,
          params: vm.$route.params,
        };
        GenericStore.set(GenericStore.StoreKey.ROUTE_BEFORE_PREVIEW, currRoute);
        const { name, query } = newpage.route;
        dispatchRouter({
          name: name as string,
          query,
        });
      } else {
        dispatchRouter(newpage?.href);
      }
      this.updateGuide();
    },
    openPreview(item: ComplexDynamicData, previewPage: number, historyId?: number, teamId?: number) {
      const path = item.toObjectPath.split('/');
      const currPath = path.slice(0, -1).join('/');
      if (IS_BROWSER) {
        this.openPreviewInBrowser(item, previewPage, historyId, teamId);
      } else {
        // 注意：vm.$route并非响应式，若要为响应式需利用vm.$route重新取值
        const currRoute = {
          name: vm.$route.name,
          query: vm.$route.query,
          params: vm.$route.params,
        };
        GenericStore.set(GenericStore.StoreKey.ROUTE_BEFORE_PREVIEW, currRoute);
        const routerName = item.previewByDoc ? 'doc' : 'media';
        const temp = {
          name: item.toObjectName,
          path: currPath,
          previewPage: `${previewPage}`,
          spaceId: item.spaceId,
          spaceOrgId: item.spaceOrgId,
          spaceTag: item.spaceTag,
        };
        if (historyId) {
          Object.assign(temp, {
            historyId: String(historyId),
          });
        }
        if (teamId) {
          Object.assign(temp, {
            teamId: String(teamId),
          });
        }
        dispatchRouter({
          name: routerName,
          query: temp,
        });
        this.updateGuide();
      }
    },

    async dynamicOpen(item: ComplexDynamicData) {
      const options = {
        path: item.toObjectPath,
        spaceId: spaceId?.value || item.spaceId,
        spaceOrgId: item?.orgId || vm.$store.getters.orgId,
      };
      const { data } = await smh.userAPI.getDirectoryOrFileInfo(options);
      const itemName = item.type === 'dir' ? i18n_t('文件夹') : i18n_t('文件');
      if (data.inRecycledDirectory) {
        tips.error(
          i18n_t('{itemName}已被删除，请前往“误删恢复”查找', {
            itemName,
          }),
        );
        return;
      }
      if (!data.inDirectory) {
        tips.error(i18n_t('文件已被删除，请恢复后重试'));
        return;
      }
      if (item.type === 'dir') {
        const spaceRouteMap = {
          team: 'teamSpace',
          personal: 'personalSpace',
          group: 'collaborationSpaceList',
        };
        if (
          options.path === vm.$route.query.path &&
          vm.$route.name === spaceRouteMap[item.spaceTag as 'team' | 'personal' | 'group']
        ) {
          tips.info(i18n_t('当前目录已打开'));
          return;
        }
        switch (item.spaceTag) {
          case 'team':
            {
              /**
               * 此处不能直接跳teamSpace路由，需判断当前共享的teamId是否其他企业空间 或者 处于teamSpace、workbench 路由
               * 是同一个跳teamSpace
               * else跳relatedToMeFileList
               */
              const flag = ['teamSpace', 'workbench', 'fileCollect'].includes(vm.$route.name as string);
              if (item.isUserBelongSpace || flag) {
                dispatchRouter({
                  name: 'teamSpace',
                  query: {
                    spaceId: options.spaceId,
                    teamId: String(item.team?.id),
                    teamPath: item.team?.path?.join('/') || item.team?.name,
                    path: options.path,
                  },
                });
              } else {
                dispatchRouter({
                  name: 'relatedToMeFileList',
                  query: {
                    spaceId: options.spaceId,
                    spaceTag: item.spaceTag,
                    teamId: String(item.team?.id),
                    path: item.toObjectPath,
                    teamPath: item.team?.path?.join('/') || item.team?.name,
                  },
                  params: {
                    isCospace: 'false',
                  },
                });
              }
            }
            break;
          case 'personal':
            if (item.user?.id === $store.getters.userId) {
              dispatchRouter({
                name: 'personalSpace',
                query: options,
              });
            } else {
              dispatchRouter({
                name: 'relatedToMeFileList',
                query: {
                  spaceTag: item.spaceTag,
                  ...options,
                },
                params: {
                  isCospace: 'false',
                },
              });
            }
            break;
          case 'group':
            $store.commit(DrawerEvents.SET_SELECTEDLIST, [
              {
                ...item.group,
                ownerId: item.group?.userId,
              },
            ]);
            dispatchRouter({
              name: 'collaborationSpaceList',
              query: {
                id: `${item.group?.id}`,
                name: `${item.group?.name}`,
                spaceId: options.spaceId,
                spaceOrgId: `${item.group?.orgId}`,
                path: options.path,
                isExternal: `${+item.isExternal}`,
              },
            });
            break;
          default:
            tips.error(i18n_t('动态数据不正确'));
        }
        ipc.emit(ipc.RENDERER_EVENTS_MAP.REPLACE_CURRENT_PATH);
      } else if (noPreview(data)) {
        tips.error(i18n_t('该格式暂不支持在线预览'));
      } else if (!can('Preview', data)) {
        tips.error(i18n_t('暂无预览权限，请联系管理员'));
      } else if (isAllowPreviewImage(data.name) && +data.size >= ConstantConfig.ciImageMaxSize) {
        // 万象最大支持32mb
        tips.error(
          i18n_t('{itemName}过大，请下载至本地查看', {
            itemName,
          }),
        );
      } else if (!$store.getters.enableDocPreview && !isAllowPrivate(data.name)) {
        if (isAllowEditDoc(data.name)) {
          tips.error(i18n_t('当前文件暂未接入文档预览与编辑服务，请下载至本地查看'));
        } else {
          tips.error(i18n_t('该文件暂不支持预览，请下载至本地查看'));
        }
      } else {
        methods.openPreview(
          {
            ...item,
            ...data,
            spaceOrgId: item.orgId,
          },
          1,
          undefined,
          data[data.spaceTag as 'team' | 'group']?.id,
        );
      }
    },
    dynamicsDrawerOpen(item: any, action: 'add' | 'remove') {
      const { name, params, query } = vm.$route;
      if (item.toObjectPath === query.path) {
        tips.info(i18n_t('当前目录已打开'));
        return;
      }
      // 初始化当前路由
      // const path = item.toObjectPath.split('/');
      let routerName = name;
      if (item?.spaceTag === 'team') {
        routerName = 'teamSpace';
        query.teamPath = item.team?.path.join('/');
        query.spaceId = item.spaceId;
        query.teamId = item.team?.id;
      } else if (item?.spaceTag === 'group') {
        routerName = 'collaborationSpace';
      }
      switch (action) {
        case 'add':
          dispatchRouter({ name: routerName as string, query: { ...query, path: item.toObjectPath } });
          ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
          break;
        default:
          break;
      }
    },
  };
  ipc.on(ipc.RENDERER_EVENTS_MAP.DRAWER_DYNAMIC_OPEN, methods.dynamicOpen);
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.DRAWER_DYNAMIC_OPEN, methods.dynamicOpen);
  });
  return {
    methods,
  };
}
